/**
 * React hook for generating exports using TanStack Query
 *
 * Provides export generation with automatic state management and error handling.
 * Supports PDF, CSV, and text formats with customizable options.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createApiClient, ApiClientError } from "@floline/api-client";
import type {
  GenerateExportRequest,
  ExportDataResponse,
} from "@floline/api-client/types";
import { API_URL } from "@/env";
import { formatDateRangeYmdDisplay } from "@/lib/date-format";
import { i18n } from "@/i18n";

// ============================================================================
// API Client
// ============================================================================

/** API client configured for export requests */
const apiClient = createApiClient(
  API_URL
);

// ============================================================================
// Query Keys
// ============================================================================

/** Query key factory for exports */
export const exportKeys = {
  all: ["exports"] as const,
  generate: (request: GenerateExportRequest) =>
    [...exportKeys.all, "generate", request] as const,
};

// ============================================================================
// Types
// ============================================================================

/** Export generation state */
export type ExportState = "idle" | "generating" | "success" | "error";

// ============================================================================
// Export Function
// ============================================================================

/**
 * Generate export data from the API
 *
 * @param request - Export request with date range and format options
 * @returns Export data response with statistics and detailed data
 */
async function generateExportData(
  request: GenerateExportRequest
): Promise<ExportDataResponse> {
  try {
    return await apiClient.generateExport(request);
  } catch (error: unknown) {
    // Rethrow with more descriptive message for common errors
    if (error instanceof ApiClientError) {
      if (error.status === 401) {
        throw new Error(i18n.t("errors.signInToExport"));
      }
      if (error.code === "INVALID_DATE_RANGE") {
        throw new Error(
          error.message || i18n.t("errors.invalidDateRange")
        );
      }
      if (error.code === "DATE_RANGE_TOO_LARGE") {
        throw new Error(
          error.message || i18n.t("errors.dateRangeTooLarge")
        );
      }
      if (error.code === "INSUFFICIENT_DATA") {
        throw new Error(
          error.message || i18n.t("errors.insufficientData")
        );
      }
      if (error.code === "EXPORT_GENERATION_FAILED") {
        throw new Error(
          error.message || i18n.t("errors.exportFailed")
        );
      }
      if (error.code === "UNSUPPORTED_FORMAT") {
        throw new Error(
          error.message || i18n.t("errors.unsupportedExportFormat")
        );
      }
    }
    throw error;
  }
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for generating exports
 *
 * Returns mutation for generating export data with automatic state management,
 * error handling, and cache invalidation.
 *
 * @example
 * ```tsx
 * const { generateExport, isGenerating, exportData, error } = useExport();
 *
 * // Generate PDF export
 * const data = await generateExport({
 *   startDate: "2026-01-01",
 *   endDate: "2026-01-31",
 *   format: "pdf",
 *   includeDetailedData: true,
 *   includeBodyDiagrams: true,
 * });
 * ```
 */
export function useExport() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: generateExportData,
    onSuccess: () => {
      // Invalidate any cached export data
      queryClient.invalidateQueries({ queryKey: exportKeys.all });
    },
  });

  // Derive state from mutation status
  const exportState: ExportState = mutation.isPending
    ? "generating"
    : mutation.isSuccess
      ? "success"
      : mutation.isError
        ? "error"
        : "idle";

  return {
    // Mutation functions
    generateExport: mutation.mutate,
    generateExportAsync: mutation.mutateAsync,

    // State
    exportState,
    isGenerating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,

    // Data
    exportData: mutation.data,
    error: mutation.error,

    // Actions
    reset: mutation.reset,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if export data is available
 *
 * @param data - Export data response
 * @returns True if export has data, false otherwise
 */
export function hasExportData(
  data: ExportDataResponse | undefined
): boolean {
  return data !== undefined && data.stats.totalCheckIns > 0;
}

/**
 * Format date range for display
 *
 * @param start - Start date (ISO string)
 * @param end - End date (ISO string)
 * @returns Formatted date range string
 *
 * @example
 * formatExportDateRange("2026-01-01", "2026-01-31")
 * // => "Jan 1, 2026 - Jan 31, 2026"
 */
export function formatExportDateRange(start: string, end: string): string {
  return formatDateRangeYmdDisplay(start, end);
}

/**
 * Calculate days in export range
 *
 * @param start - Start date (ISO string)
 * @param end - End date (ISO string)
 * @returns Number of days in the range (inclusive)
 *
 * @example
 * getDaysInExportRange("2026-01-01", "2026-01-31")
 * // => 31
 */
export function getDaysInExportRange(start: string, end: string): number {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffTime = endDate.getTime() - startDate.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
}
